import React, { useState, useEffect } from 'react';
import { Loader2, FileText, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * DocumentsPanel — gedeeld documenten-tabblad (upload + lijst) voor de hele
 * pipeline: lead, prospect, onboarding en client. Documenten hangen aan
 * dezelfde lead-rij via /api/v1/documents/lead/{id}; de lijst toont ALLE
 * documenten van het dossier (ongeacht categorie), zodat niets verdwijnt
 * bij een fase-overgang. Nieuwe uploads krijgen de meegegeven categorie.
 */

const getToken = () => sessionStorage.getItem('auth_token');

export default function DocumentsPanel({ leadId, category = 'general', className = '' }) {
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3000);
  };

  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      const resp = await fetch(`/api/v1/documents/lead/${leadId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setDocuments((data.documents || data || []).map(d => ({
        id: d.id,
        name: d.original_filename || d.name,
        size: d.file_size ? (d.file_size / 1024).toFixed(1) : '?',
        date: new Date(d.created_at || d.uploaded_at),
        type: d.category || d.file_type || 'file',
        path: d.file_path,
      })));
    } catch {
      setDocuments([]);
    }
    setDocsLoading(false);
  };

  useEffect(() => {
    if (leadId) fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleFileUpload = async (files) => {
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('lead_id', leadId);
        formData.append('category', category);

        const resp = await fetch('/api/v1/documents/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData,
        });

        if (resp.ok) {
          showMsg('success', `${file.name} geüpload`);
          fetchDocuments();
        } else {
          showMsg('error', `Upload mislukt: ${file.name}`);
        }
      } catch {
        showMsg('error', `Upload mislukt: ${file.name}`);
      }
    }
  };

  const inputId = `doc-upload-${leadId}`;

  return (
    <div className={`space-y-6 ${className}`}>
      {msg && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
          msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-[#f0fdf4] text-[#16a34a]'
        }`}>
          {msg.type === 'error' ? <AlertCircle size={13} /> : <CheckCircle size={13} />}
          {msg.text}
        </div>
      )}

      <div
        onDrop={e => { e.preventDefault(); handleFileUpload(Array.from(e.dataTransfer.files)); }}
        onDragOver={e => e.preventDefault()}
        className="border-2 border-dashed border-[#a4abbe] rounded-xl p-8 text-center hover:border-[#3d61a4] hover:bg-[#eef2fa] transition-all cursor-pointer"
      >
        <input
          type="file"
          multiple
          onChange={e => { handleFileUpload(Array.from(e.target.files)); e.target.value = ''; }}
          className="hidden"
          id={inputId}
        />
        <label htmlFor={inputId} className="cursor-pointer block">
          <FileText size={36} className="mx-auto mb-3 text-[#a4abbe]" />
          <p className="text-[#566079] font-medium text-sm">Sleep bestanden hierheen of klik om te uploaden</p>
          <p className="text-xs text-[#a4abbe] mt-1">PDF, Word, Excel, afbeeldingen</p>
        </label>
      </div>

      {docsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-[#3d61a4]" />
        </div>
      ) : documents.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-[#566079] uppercase tracking-wider">
            Bestanden ({documents.length})
          </h3>
          {documents.map(doc => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-[#f7f8fc] rounded-lg border border-[#e8eaf2]">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-[#3d61a4]" />
                <div>
                  <p className="text-sm font-medium text-[#011745]">{doc.name}</p>
                  <p className="text-xs text-[#a4abbe]">
                    {doc.size} KB • {doc.date instanceof Date && !isNaN(doc.date) ? doc.date.toLocaleDateString('nl-NL') : ''}
                    {doc.type ? ` • ${doc.type}` : ''}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#7b859e] text-sm">Nog geen documenten geüpload</p>
      )}
    </div>
  );
}
